import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Radio,
  TextField,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  useExportCmpUserActivityLogCsvLazyQuery,
  useGetCmpUserActivityLogQuery,
} from "../../../../../graphql/types";
import { commonStyles } from "../../../../common/commonStyles";
import { AnchoredMenu } from "../../../../common/dashboard/AnchoredMenu";
import CalendarDateInput, {
  isCalendarDateValue,
} from "../../../../common/dashboard/CalendarDateInput";
import {
  DataTable,
  DataTableColumn,
} from "../../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
} from "../../../../common/dashboard/FilterBar";
import {
  getTimeZoneCalendarDateEndUtcMilli,
  getTimeZoneCalendarDateStartUtcMilli,
} from "../../../../common/dashboard/formatters";
import { CmpUserActivityViewModel } from "./types";
import {
  downloadCsv,
  getOperationErrorMessage,
  PRESERVE_ERROR_FOR_CALLER_CONTEXT,
} from "./uiUtilities";
import { normalizeCmpTimeZone } from "../../../../../utils/cmpTimeZones";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import { buildStaffErrorFallback, STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";

export type ActivityLogViewProps = {
  onBack: () => void;
  timeZone?: string;
};

type ActivityColumn =
  | "dateTime"
  | "user"
  | "relatedRecord"
  | "section"
  | "action"
  | "activityDetails";

type SelectOption = {
  value: string;
  label: string;
};

// Monday 13358276122: "Users and Permissions" is now "Employees and
// Permissions". Saved activity rows keep their stored section; only the
// shown label changes, and filtering still sends the stored value.
const activitySectionLabels: Record<string, string> = {
  "Users & Permissions": "Employees & Permissions",
};

export const getActivitySectionLabel = (section: string) =>
  activitySectionLabels[section] ?? section;

const addOptions = (
  existing: readonly SelectOption[],
  additions: readonly SelectOption[]
): SelectOption[] => {
  const values = new Map(existing.map((option) => [option.value, option]));

  additions.forEach((option) => values.set(option.value, option));

  return Array.from(values.values()).sort((left, right) =>
    left.label.localeCompare(right.label)
  );
};

export const ActivityLogView = ({
  onBack,
  timeZone,
}: ActivityLogViewProps) => {
  const noun = useCmpOrganizationNoun();
  const resolvedTimeZone = normalizeCmpTimeZone(
    timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  ).timeZone;
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState("");
  const [section, setSection] = useState("");
  const [relatedRecordSearch, setRelatedRecordSearch] = useState("");
  const deferredRelatedRecordSearch = useDeferredValue(
    relatedRecordSearch.trim()
  );
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const [sectionAnchor, setSectionAnchor] = useState<HTMLElement | null>(null);
  const [relatedAnchor, setRelatedAnchor] = useState<HTMLElement | null>(null);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [sectionOptions, setSectionOptions] = useState<SelectOption[]>([]);
  const [exportError, setExportError] = useState("");

  const filterInput = useMemo(
    () => ({
      ...(deferredSearch ? { search: deferredSearch } : {}),
      // Partial typed text is not sent until it is a complete date.
      ...(isCalendarDateValue(fromDate)
        ? {
            fromUtcMilli: getTimeZoneCalendarDateStartUtcMilli(
              fromDate,
              resolvedTimeZone
            ),
          }
        : {}),
      ...(isCalendarDateValue(toDate)
        ? {
            toUtcMilli: getTimeZoneCalendarDateEndUtcMilli(
              toDate,
              resolvedTimeZone
            ),
          }
        : {}),
      ...(userId ? { userId } : {}),
      ...(section ? { section } : {}),
      ...(deferredRelatedRecordSearch
        ? { relatedRecordSearch: deferredRelatedRecordSearch }
        : {}),
    }),
    [
      deferredRelatedRecordSearch,
      deferredSearch,
      fromDate,
      resolvedTimeZone,
      section,
      toDate,
      userId,
    ]
  );

  const { data, loading, error, refetch } = useGetCmpUserActivityLogQuery({
    variables: { input: filterInput },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [exportActivityLog, { loading: exportLoading }] =
    useExportCmpUserActivityLogCsvLazyQuery({
      fetchPolicy: "network-only",
      context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    });

  const rows: CmpUserActivityViewModel[] = useMemo(
    () =>
      (data?.getCmpUserActivityLog ?? []).map((activity) => ({
        id: activity.id,
        actorUserId: activity.actorUserId,
        actorDisplayName: activity.actorDisplayName,
        relatedRecordId: activity.relatedRecordId,
        relatedRecordDisplayName: activity.relatedRecordDisplayName,
        section: activity.section,
        action: activity.action,
        activityDetails: activity.activityDetails,
        createdAtUtcMilli: activity.createdAtUtcMilli,
      })),
    [data]
  );

  useEffect(() => {
    setUserOptions((current) =>
      addOptions(
        current,
        rows.map((row) => ({
          value: row.actorUserId,
          label: row.actorDisplayName,
        }))
      )
    );
    setSectionOptions((current) =>
      addOptions(
        current,
        rows.map((row) => ({ value: row.section, label: getActivitySectionLabel(row.section) }))
      )
    );
  }, [rows]);

  const dateTimeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-CA", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: resolvedTimeZone,
      }),
    [resolvedTimeZone]
  );

  const columns = useMemo<
    DataTableColumn<CmpUserActivityViewModel, ActivityColumn>[]
  >(
    () => [
      {
        key: "dateTime",
        header: "DATE & TIME",
        width: "15%",
        render: (activity) => (
          <span className="font-extrabold text-gray-950">
            {dateTimeFormatter.format(
              new Date(activity.createdAtUtcMilli)
            )}
          </span>
        ),
      },
      {
        key: "user",
        header: "USER",
        width: "14%",
        render: (activity) => activity.actorDisplayName,
      },
      {
        key: "relatedRecord",
        header: "RELATED RECORD",
        width: "16%",
        render: (activity) => activity.relatedRecordDisplayName || "—",
      },
      {
        key: "section",
        header: "SECTION",
        width: "13%",
        render: (activity) => getActivitySectionLabel(activity.section),
      },
      {
        key: "action",
        header: "ACTION",
        width: "12%",
        render: (activity) => activity.action,
      },
      {
        key: "activityDetails",
        header: "ACTIVITY DETAILS",
        width: "30%",
        render: (activity) => activity.activityDetails,
      },
    ],
    [dateTimeFormatter]
  );

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(fromDate) ||
    Boolean(toDate) ||
    Boolean(userId) ||
    Boolean(section) ||
    Boolean(relatedRecordSearch);

  const exportCsv = async () => {
    setExportError("");

    try {
      const result = await exportActivityLog({
        variables: { input: filterInput },
      });
      const exported = result.data?.exportCmpUserActivityLogCsv;

      if (!exported) {
        throw new Error("The activity log export did not return a file.");
      }

      downloadCsv({ csv: exported.csv, fileName: exported.fileName });
    } catch (operationError) {
      setExportError(
        getOperationErrorMessage(
          operationError,
          buildStaffErrorFallback("export the activity log")
        )
      );
    }
  };

  return (
    <section aria-labelledby="user-activity-log-heading">
      <Button
        type="button"
        onClick={onBack}
        startIcon={<ArrowBackOutlinedIcon />}
        sx={{
          color: commonStyles.cpaGold,
          fontSize: 17,
          fontWeight: 900,
          mb: 4,
          px: 0,
          textTransform: "none",
          "&:hover": { backgroundColor: "transparent", color: commonStyles.cpaGoldDark },
        }}
      >
        Go Back
      </Button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            id="user-activity-log-heading"
            className="text-2xl font-extrabold uppercase text-gray-950"
          >
            Employee Activity Log
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-700">
            Review activity across your Chamber Member Pro dashboard.
          </p>
        </div>
        <Button
          type="button"
          variant="contained"
          disabled={exportLoading}
          onClick={exportCsv}
          startIcon={
            exportLoading ? (
              <CircularProgress color="inherit" size={18} />
            ) : (
              <DownloadOutlinedIcon />
            )
          }
          sx={{
            backgroundColor: commonStyles.cpaGold,
            fontWeight: 900,
            minHeight: 52,
            minWidth: { sm: 220 },
            textTransform: "none",
            "&:hover": { backgroundColor: commonStyles.cpaGoldDark },
          }}
        >
          Export Activity Log
        </Button>
      </div>

      <div className="mt-7 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        {exportError ? (
          <Alert
            severity="error"
            className="mb-5"
            onClose={() => setExportError("")}
          >
            {exportError}
          </Alert>
        ) : null}
        {error ? (
          <Alert
            severity="error"
            className="mb-5"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {`We could not load the activity log. Click Retry. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`}
          </Alert>
        ) : null}

        <FilterBar className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <FilterSearchField
              value={search}
              onChange={setSearch}
              placeholder="Search by Activity Details"
              ariaLabel="Search user activity details"
            />
            <ClearFiltersButton
              disabled={!hasActiveFilters}
              onClick={() => {
                setSearch("");
                setFromDate("");
                setToDate("");
                setUserId("");
                setSection("");
                setRelatedRecordSearch("");
              }}
              wrapperClassName="xl:w-[210px]"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <FilterMenuButton
              label={
                fromDate || toDate
                  ? `${fromDate || "Any date"} – ${toDate || "Any date"}`
                  : "Filter by Date"
              }
              active={Boolean(fromDate || toDate)}
              startIcon={<CalendarTodayOutlinedIcon />}
              onOpen={setDateAnchor}
            />
            <FilterMenuButton
              label={
                userOptions.find((option) => option.value === userId)?.label ??
                "Filter by Employee"
              }
              active={Boolean(userId)}
              onOpen={setUserAnchor}
            />
            <FilterMenuButton
              label={section ? getActivitySectionLabel(section) : "Filter by Section"}
              active={Boolean(section)}
              onOpen={setSectionAnchor}
            />
            <FilterMenuButton
              label={relatedRecordSearch || "Filter by Related Record"}
              active={Boolean(relatedRecordSearch)}
              onOpen={setRelatedAnchor}
            />
          </div>
        </FilterBar>

        <AnchoredMenu
          anchorEl={dateAnchor}
          open={Boolean(dateAnchor)}
          onClose={() => setDateAnchor(null)}
          variant="panel"
          PaperProps={{ "aria-label": "Filter by date", sx: { p: 2, width: 320 } }}
        >
          <div className="space-y-4">
            <div>
              <label
                htmlFor="activity-log-from-date"
                className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]"
              >
                From
              </label>
              <CalendarDateInput
                id="activity-log-from-date"
                ariaLabel="From"
                mode="fullDate"
                iconPosition="left"
                value={fromDate}
                onChange={setFromDate}
              />
            </div>
            <div>
              <label
                htmlFor="activity-log-to-date"
                className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]"
              >
                Through
              </label>
              <CalendarDateInput
                id="activity-log-to-date"
                ariaLabel="Through"
                mode="fullDate"
                iconPosition="left"
                value={toDate}
                minDate={isCalendarDateValue(fromDate) ? fromDate : undefined}
                onChange={setToDate}
              />
            </div>
            <Button
              type="button"
              fullWidth
              onClick={() => setDateAnchor(null)}
              sx={{ color: commonStyles.cpaGoldDark, fontWeight: 800 }}
            >
              Done
            </Button>
          </div>
        </AnchoredMenu>

        <AnchoredMenu
          anchorEl={userAnchor}
          open={Boolean(userAnchor)}
          onClose={() => setUserAnchor(null)}
          PaperProps={{ sx: { maxHeight: 360, minWidth: 240 } }}
        >
          <MenuItem
            onClick={() => {
              setUserId("");
              setUserAnchor(null);
            }}
          >
            <Radio checked={!userId} size="small" />
            All Users
          </MenuItem>
          {userOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                setUserId(option.value);
                setUserAnchor(null);
              }}
            >
              <Radio checked={userId === option.value} size="small" />
              {option.label}
            </MenuItem>
          ))}
        </AnchoredMenu>

        <AnchoredMenu
          anchorEl={sectionAnchor}
          open={Boolean(sectionAnchor)}
          onClose={() => setSectionAnchor(null)}
          PaperProps={{ sx: { maxHeight: 360, minWidth: 240 } }}
        >
          <MenuItem
            onClick={() => {
              setSection("");
              setSectionAnchor(null);
            }}
          >
            <Radio checked={!section} size="small" />
            All Sections
          </MenuItem>
          {sectionOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                setSection(option.value);
                setSectionAnchor(null);
              }}
            >
              <Radio checked={section === option.value} size="small" />
              {option.label}
            </MenuItem>
          ))}
        </AnchoredMenu>

        <AnchoredMenu
          anchorEl={relatedAnchor}
          open={Boolean(relatedAnchor)}
          onClose={() => setRelatedAnchor(null)}
          variant="panel"
          PaperProps={{ "aria-label": "Filter by related record", sx: { p: 2, width: 320 } }}
        >
          <div>
            <TextField
              autoFocus
              label="Related record"
              value={relatedRecordSearch}
              onChange={({ target }) =>
                setRelatedRecordSearch(target.value)
              }
              placeholder="Member, event, invoice..."
              fullWidth
              size="small"
            />
            <Button
              type="button"
              fullWidth
              onClick={() => setRelatedAnchor(null)}
              sx={{ color: commonStyles.cpaGoldDark, fontWeight: 800, mt: 2 }}
            >
              Done
            </Button>
          </div>
        </AnchoredMenu>

        <DataTable
          ariaLabel="User activity log"
          columns={columns}
          rows={rows}
          getRowKey={(activity) => activity.id}
          showLoadingRow={loading && rows.length === 0}
          loadingRowContent={
            <div className="py-8 text-center text-sm font-semibold text-gray-500">
              Loading user activity...
            </div>
          }
          showEmptyRow={!loading && !error && rows.length === 0}
          emptyRowContent={
            <div className="py-8 text-center text-sm font-semibold text-gray-500">
              {hasActiveFilters
                ? "No activity matches the current search and filters."
                : "No user activity has been recorded yet."}
            </div>
          }
          tableSx={{
            minWidth: 1180,
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              borderColor: "#E5E7EB",
              overflowWrap: "anywhere",
              px: 1.5,
              py: 2.5,
              verticalAlign: "top",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 800,
              overflowWrap: "normal",
              whiteSpace: "nowrap",
            },
          }}
        />
        <p className="mt-3 text-xs leading-5 text-gray-500">
          Results are drawn from the 5,000 most recent {noun.singular} activities and
          show up to 1,000 matches. Exports use the same recent activity
          window.
        </p>
      </div>
    </section>
  );
};

export default ActivityLogView;
